import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import {trackPanelQuickAdd} from '../../../services/analytics/panelQuickAddEvents';
import * as PanelBankSectionConfigTypes from '../../../state/views/panelBankSectionConfig/types';
import {AddPanel} from '../../PanelBank/types';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawerContextProvider';

export type QuickAddKeys = Array<[string, AddPanel['spec']]>;

type OpenQuickAdd = (args: {
  /** for analytics; where the open action was triggered */
  location: string;
  sectionRef?: PanelBankSectionConfigTypes.Ref;
  searchStr?: string;
  onBack?: () => void;
}) => void;

type PanelsQuickAddContextValue = {
  openQuickAdd: OpenQuickAdd;
  closeQuickAdd: () => void;
  initialSearchStr: string;
  onBack?: () => void;
};
type PanelsQuickAddContextProviderProps = {
  children: React.ReactNode;
};

const PanelsQuickAddContext = createContext<
  PanelsQuickAddContextValue | undefined
>(undefined);

export const PanelsQuickAddContextProvider = ({
  children,
}: PanelsQuickAddContextProviderProps) => {
  const {openWorkspaceDrawer, closeWorkspaceDrawer} =
    useWorkspaceDrawerContext();

  // When opening the quick add drawer, a searchStr may be
  // specified to populate the search box with an initial value.
  // PERF NOTE: considered lifting searchStr state out of PanelsQuickAdd
  // and into this context, but that would cause the entire panel bank
  // to re-render on every search. Decided it's best to let the quick
  // add comp manage its search state locally.
  const [initialSearchStr, setInitialSearchStr] = useState('');

  const [onBack, setOnBack] = useState<() => void | undefined>();

  const openQuickAdd = useCallback<OpenQuickAdd>(
    ({location, sectionRef, searchStr = '', onBack: newOnBack}) => {
      trackPanelQuickAdd({
        action: 'Drawer opened',
        scope: !sectionRef ? 'global' : 'section',
        location,
      });
      openWorkspaceDrawer('panels-quick-add', sectionRef);
      setInitialSearchStr(searchStr);
      setOnBack(() => newOnBack);
    },
    [openWorkspaceDrawer]
  );

  const closeQuickAdd = useCallback(() => {
    closeWorkspaceDrawer();
    setInitialSearchStr('');
    setOnBack(undefined);
  }, [closeWorkspaceDrawer]);

  const value = useMemo(
    () => ({
      openQuickAdd,
      closeQuickAdd,
      initialSearchStr,
      onBack,
    }),
    [openQuickAdd, closeQuickAdd, initialSearchStr, onBack]
  );

  return (
    <PanelsQuickAddContext.Provider value={value}>
      {children}
    </PanelsQuickAddContext.Provider>
  );
};

export const usePanelsQuickAddContext = (): PanelsQuickAddContextValue => {
  const value = useContext(PanelsQuickAddContext);
  if (value == null) {
    throw new Error(
      'usePanelsQuickAddContext must be used within a PanelsQuickAddContextProvider'
    );
  }
  return value;
};
